import {
    CheckCircle2, FileText, Palette, Layout, Type, Layers,
    Navigation, BarChart3, Divide, Image, AlertTriangle, Lightbulb,
    Clock, Code2, Globe
} from 'lucide-react';

/* ──────────────────────────────────────────────────────────────
 * BuildReport — structured post-build summary
 * Props:
 *   buildSummary   – { projectName, totalFiles, pageCount, componentCount, hasCSS, hasTests, fileList, moreFiles, timestamp }
 *   layoutPlan     – resolved layout plan from SSE complete event
 *   visualProfile  – { archetype, variant, hero_layout, typography, shape, nav_style, divider, stats_layout, hero_media }
 *   planner        – planner object with tasks[]
 *   research       – research brief
 *   buildMode      – 'static' | 'full_ai'
 *   artifacts      – artifact contract data
 * ────────────────────────────────────────────────────────────── */

const Section = ({ icon: Icon, title, children, accentClass = 'text-slate-400' }) => (
    <div className="rounded-lg border border-slate-700/40 bg-slate-900/40 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-700/30 bg-slate-800/30">
            <Icon className={`h-4 w-4 ${accentClass}`} />
            <span className="text-sm font-semibold text-slate-200">{title}</span>
        </div>
        <div className="px-4 py-3">{children}</div>
    </div>
);

const Stat = ({ label, value, accent = false }) => (
    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
        <div className={`text-xl font-bold ${accent ? 'text-orange-400' : 'text-white'}`}>{value}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
    </div>
);

const Row = ({ label, value }) => (
    <div className="flex items-center gap-2 text-xs py-1">
        <span className="text-slate-500 w-28 shrink-0">{label}</span>
        <span className="text-slate-200 font-mono truncate">{value || '—'}</span>
    </div>
);

const BuildReport = ({
    buildSummary,
    layoutPlan,
    visualProfile,
    planner,
    research,
    buildMode,
    artifacts,
}) => {
    if (!buildSummary) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <FileText className="h-8 w-8 mb-2 opacity-40" />
                <span>No build report yet</span>
                <span className="text-xs mt-1">Complete a build to see the report</span>
            </div>
        );
    }

    /* Derive visual profile from layoutPlan if not provided separately */
    const vp = visualProfile || {};
    const lp = layoutPlan || {};

    /* attempted task stats */
    const taskStats = (() => {
        if (!planner?.tasks?.length) return null;
        const tasks = planner.tasks;
        const completed = tasks.filter(t => t.status === 'completed' || t.status === 'complete').length;
        const failed = tasks.filter(t => t.status === 'failed').length;
        return { total: tasks.length, completed, failed };
    })();

    /* suggested next edits */
    const suggestions = [];
    if (!buildSummary.hasCSS) suggestions.push('Add custom CSS styling for a polished look');
    if (buildSummary.pageCount <= 1) suggestions.push('Add more pages (About, Contact, Services)');
    if (!buildSummary.hasTests) suggestions.push('Add test coverage for reliability');
    if (buildMode === 'static') suggestions.push('Try Full AI mode for external fonts and live images');
    if (buildSummary.totalFiles < 5) suggestions.push('Expand with additional components and sections');
    if (suggestions.length === 0) suggestions.push('Deploy to production!', 'Create a snapshot as a restore point');

    /* warnings */
    const warnings = [];
    if (taskStats?.failed > 0) warnings.push(`${taskStats.failed} task(s) failed or blocked during build`);
    if (buildSummary.totalFiles === 0) warnings.push('No files were generated');
    if (!buildSummary.hasCSS) warnings.push('No CSS file detected — styling may be inline or missing');

    return (
        <div className="flex flex-col h-full bg-slate-950/80 rounded-lg border border-slate-700/50 overflow-hidden">
            {/* header */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-700/50 bg-gradient-to-r from-green-900/30 to-emerald-900/20 shrink-0">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <span className="text-sm font-semibold text-green-300 tracking-wide">Build Report</span>
                <span className="ml-auto text-xs text-slate-500">{buildSummary.timestamp}</span>
            </div>

            {/* scrollable body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">

                {/* ── Summary Stats ── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Stat label="Total Files" value={buildSummary.totalFiles} accent />
                    <Stat label="Pages" value={buildSummary.pageCount} />
                    <Stat label="Components" value={buildSummary.componentCount} />
                    <Stat label="CSS" value={buildSummary.hasCSS ? '✓' : '—'} />
                </div>

                {/* ── Build Mode ── */}
                <div className="flex items-center gap-2 text-xs text-slate-400 px-1">
                    <Globe className="h-3.5 w-3.5" />
                    <span>Mode: <span className="text-slate-200 font-medium">{buildMode === 'full_ai' ? 'Full AI (Online)' : 'Fortress (Offline)'}</span></span>
                    {taskStats && (
                        <>
                            <span className="text-slate-600 mx-1">|</span>
                            <span>Tasks: {taskStats.completed}/{taskStats.total} completed</span>
                        </>
                    )}
                </div>

                {/* ── Visual Profile ── */}
                {(lp.industry || lp.hero_variant || vp.archetype || vp.typography) && (
                    <Section icon={Palette} title="Visual Profile" accentClass="text-purple-400">
                        <div className="space-y-0.5">
                            {vp.archetype && <Row label="Archetype" value={vp.archetype} />}
                            {vp.variant && <Row label="Variant" value={vp.variant} />}
                            {lp.industry && <Row label="Industry" value={lp.industry} />}
                            {(vp.hero_layout || lp.hero_variant) && <Row label="Hero" value={vp.hero_layout || lp.hero_variant} />}
                            {vp.typography && <Row label="Typography" value={vp.typography} />}
                            {vp.shape && <Row label="Shape" value={vp.shape} />}
                            {(vp.nav_style || lp.nav_variant) && <Row label="Navigation" value={vp.nav_style || lp.nav_variant} />}
                            {lp.grid_style && <Row label="Grid" value={lp.grid_style} />}
                            {vp.divider && <Row label="Divider" value={vp.divider} />}
                            {vp.stats_layout && <Row label="Stats Layout" value={vp.stats_layout} />}
                            {lp.cta_variant && <Row label="CTA" value={lp.cta_variant} />}
                            {lp.footer_variant && <Row label="Footer" value={lp.footer_variant} />}
                            {vp.hero_media !== undefined && <Row label="Hero Media" value={vp.hero_media ? 'On' : 'Off'} />}
                            {lp.seed_hex && <Row label="Seed" value={lp.seed_hex} />}
                        </div>
                    </Section>
                )}

                {/* ── Sections Rendered ── */}
                {lp.sections && lp.sections.length > 0 && (
                    <Section icon={Layers} title="Sections" accentClass="text-blue-400">
                        <div className="flex flex-wrap gap-1.5">
                            {lp.sections.map((s, i) => (
                                <span key={i} className="px-2 py-0.5 bg-slate-800/60 border border-slate-700/40 rounded text-xs text-slate-300 font-mono">
                                    {s}
                                </span>
                            ))}
                        </div>
                    </Section>
                )}

                {/* ── Files Created ── */}
                <Section icon={Code2} title="Files Created" accentClass="text-orange-400">
                    <div className="space-y-1">
                        {buildSummary.fileList.map((file, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                                <FileText className="h-3 w-3 text-slate-600" />
                                <span className="text-slate-300 font-mono truncate">{file}</span>
                            </div>
                        ))}
                        {buildSummary.moreFiles > 0 && (
                            <div className="text-[11px] text-slate-500 pt-1">
                                &hellip;and {buildSummary.moreFiles} more file{buildSummary.moreFiles > 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                </Section>

                {/* ── Research Highlights ── */}
                {research && Array.isArray(research.summary) && research.summary.length > 0 && (
                    <Section icon={Lightbulb} title="Research Highlights" accentClass="text-emerald-400">
                        <div className="space-y-1">
                            {research.summary.slice(0, 5).map((line, i) => (
                                <div key={i} className="text-xs text-slate-300 flex items-start gap-1.5">
                                    <span className="text-emerald-500 mt-0.5">•</span>
                                    <span>{line}</span>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                {/* ── Warnings ── */}
                {warnings.length > 0 && (
                    <Section icon={AlertTriangle} title="Warnings" accentClass="text-yellow-400">
                        <div className="space-y-1">
                            {warnings.map((w, i) => (
                                <div key={i} className="text-xs text-yellow-200/80 flex items-start gap-1.5">
                                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-yellow-500" />
                                    <span>{w}</span>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                {/* ── Suggested Next Steps ── */}
                <Section icon={Lightbulb} title="Next Steps" accentClass="text-amber-400">
                    <div className="space-y-1.5">
                        {suggestions.slice(0, 5).map((s, i) => (
                            <div key={i} className="text-xs text-slate-300 flex items-start gap-1.5">
                                <span className="text-amber-500 font-bold mt-0.5">{i + 1}.</span>
                                <span>{s}</span>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>
        </div>
    );
};

export default BuildReport;
